// React libs
import { useCallback, useEffect, useMemo } from 'react'
import moment from 'moment'
import { keyBy, sortBy } from 'lodash'
// Types
import * as CoreTypes from '../../../../Core/Data/Models/Core.type'
// Services
import DatavizService from '../Services/DatavizService';
// Hooks
import usePoiFilters from './PoiFilters'
import usePhaseTypes from '../../../../Core/Data/Hooks/PhaseTypes';
import useGenericData from '../../../../Core/Data/Hooks/GenericData';
// Utils
import { ifDef } from '../../../../Core/Utils/Misc'

const useData = (dateStart: string, dateEnd: string, filters: any) => {
  // Variable
  const projectEvolutionMetrics = useGenericData(() => DatavizService.getProjectEvolutionMetrics(dateStart, dateEnd, filters))
  const phaseTypes = usePhaseTypes()

  const isLoading = projectEvolutionMetrics.isLoading || phaseTypes.isLoading
  const refreshTime = useMemo(() => Math.max(projectEvolutionMetrics.refreshTime, phaseTypes.refreshTime), [phaseTypes.refreshTime, projectEvolutionMetrics.refreshTime])
  const phaseTypesById = useMemo(() => keyBy(phaseTypes.data, 'id'), [phaseTypes])
  const legends = useMemo(() => {
    if (projectEvolutionMetrics.data == null || projectEvolutionMetrics.data.length === 0) {
      return []
    }

    const [entry] = projectEvolutionMetrics.data
    return Object.keys(entry).filter(key => key !== 'date').map(id => phaseTypesById[id]?.label)
  }, [phaseTypesById, projectEvolutionMetrics.data])
  const { dates, series } = useMemo(() => {
    const dates: string[] = []
    const dataByLegend: { [key: string]: any } = {}
    sortBy(projectEvolutionMetrics.data, 'date').forEach(({ date, ...phaseIds }) => {
      dates.push(moment(date).format('MMM YY'))

      Object.entries(phaseIds).forEach(([phaseId, value]) => {
        const phaseLabel = phaseTypesById[phaseId]?.label
        if (dataByLegend[phaseLabel] === undefined) {
          dataByLegend[phaseLabel] = []
        }
        dataByLegend[phaseLabel].push(value)
      })
    })
    return {
      dates,
      series: Object.entries(dataByLegend).map(([legend, data]) => ({
        legend,
        data
      }))
    }
  }, [phaseTypesById, projectEvolutionMetrics.data])

  // Handlers
  const refresh = useCallback(() => {
    if (!isLoading) {
      projectEvolutionMetrics.refresh()
      phaseTypes.refresh()
    }
  }, [isLoading, phaseTypes, projectEvolutionMetrics])

  return {
    isLoading,
    refresh,
    refreshTime,
    data: {
      legends,
      dates,
      series
    },
  }
}

const useProjectEvolutionMetrics = (dateStart: string, dateEnd: string): CoreTypes.IDatavizHook => {
  // Variables
  const { filters, ...filtersProps } = usePoiFilters('project')
  const data = useData(dateStart, dateEnd, filters)
  const stringFilters = useMemo(() => ifDef(filters, JSON.stringify), [filters])

  // Effects
  useEffect(() => {
    data.refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStart, dateEnd, stringFilters])

  return {
    ...data,
    filter: filtersProps
  }
}

export default useProjectEvolutionMetrics